import type { TourismOverviewParams } from '../api/tourism';

/**
 * Merkmalsfilter der Seite „Daten".
 *
 * Bewusst ein eigener Typ neben `types/inserateFilter.ts`: die Sachbearbeiter-
 * Filter (Status, Zuordnung, Vermieter, Verfahren) haben auf einem Dashboard
 * für Tourismusbehörden nichts zu suchen, und umgekehrt fehlen dort Ortsteil,
 * Anbieter und Gruppen. Die Helfer-Struktur ist identisch, damit beide gleich
 * zu lesen sind.
 *
 * Zahlenfelder sind Strings, weil sie direkt an `<input type="number">` hängen —
 * ein leeres Feld ist '' und nicht 0.
 */
export interface TourismFilter {
  listingTypes: string[];
  neighborhoods: string[];
  platforms: string[];
  zipcodes: string[];
  gruppeIds: number[];
  priceMin: string;
  priceMax: string;
  sizeMin: string;
  sizeMax: string;
  guestsMin: string;
  guestsMax: string;
  bedroomsMin: string;
  bedroomsMax: string;
  ratingMin: string;
  ratingMax: string;
}

export const DEFAULT_TOURISM_FILTER: TourismFilter = {
  listingTypes: [],
  neighborhoods: [],
  platforms: [],
  zipcodes: [],
  gruppeIds: [],
  priceMin: '',
  priceMax: '',
  sizeMin: '',
  sizeMax: '',
  guestsMin: '',
  guestsMax: '',
  bedroomsMin: '',
  bedroomsMax: '',
  ratingMin: '',
  ratingMax: '',
};

const num = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const list = <T,>(values: T[]): T[] | undefined => (values.length ? values : undefined);

export function buildTourismParams(filter: TourismFilter): TourismOverviewParams {
  return {
    listing_types: list(filter.listingTypes),
    neighborhoods: list(filter.neighborhoods),
    platforms: list(filter.platforms),
    zipcodes: list(filter.zipcodes),
    gruppe_ids: list(filter.gruppeIds),
    price_min: num(filter.priceMin),
    price_max: num(filter.priceMax),
    size_min: num(filter.sizeMin),
    size_max: num(filter.sizeMax),
    guests_min: num(filter.guestsMin),
    guests_max: num(filter.guestsMax),
    bedrooms_min: num(filter.bedroomsMin),
    bedrooms_max: num(filter.bedroomsMax),
    rating_min: num(filter.ratingMin),
    rating_max: num(filter.ratingMax),
  };
}

export function isTourismFilterActive(filter: TourismFilter): boolean {
  return (
    filter.listingTypes.length > 0 ||
    filter.neighborhoods.length > 0 ||
    filter.platforms.length > 0 ||
    filter.zipcodes.length > 0 ||
    filter.gruppeIds.length > 0 ||
    [
      filter.priceMin, filter.priceMax, filter.sizeMin, filter.sizeMax,
      filter.guestsMin, filter.guestsMax, filter.bedroomsMin, filter.bedroomsMax,
      filter.ratingMin, filter.ratingMax,
    ].some((v) => v.trim() !== '')
  );
}

/** Zahl der aktiven Einstellungen je Dropdown – für das Badge am Filterknopf. */
export function computeTourismBadgeCounts(filter: TourismFilter) {
  const ranges = (...values: string[]) => values.filter((v) => v.trim() !== '').length;
  return {
    objekt:
      filter.listingTypes.length +
      ranges(
        filter.sizeMin, filter.sizeMax,
        filter.guestsMin, filter.guestsMax,
        filter.bedroomsMin, filter.bedroomsMax,
      ),
    preis: ranges(filter.priceMin, filter.priceMax, filter.ratingMin, filter.ratingMax),
    lage: filter.neighborhoods.length + filter.zipcodes.length,
    anbieter: filter.platforms.length,
    gruppen: filter.gruppeIds.length,
  };
}
